import { cva, type VariantProps } from 'class-variance-authority'

import type { BadgeColor } from '@/lib/badge'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center rounded-full py-1 px-3 text-ui-label-l1 leading-ui-label-l1 font-semibold whitespace-nowrap [&>svg]:pointer-events-none [&>svg]:size-4! *:[span]:px-1',
  {
    variants: {
      color: {
        primary: 'bg-main-support-surface-primary text-main-support-on-surface-on-primary',
        secondary: 'bg-main-support-surface-secondary text-main-support-on-surface-on-secondary',
        neutral: 'bg-main-support-surface-neutral text-main-support-on-surface-on-neutral',
        fuchsia: 'bg-main-support-surface-fuchsia text-main-support-on-surface-on-fuchsia',
        pink: 'bg-main-support-surface-pink text-main-support-on-surface-on-pink',
        rose: 'bg-main-support-surface-red text-main-support-on-surface-on-rose',
        red: 'bg-main-support-surface-red text-main-support-on-surface-on-rose',
        orange: 'bg-main-support-surface-orange text-main-support-on-surface-on-orange',
        amber: 'bg-main-support-surface-amber text-main-support-on-surface-on-amber',
        yellow: 'bg-main-support-surface-yellow text-main-support-on-surface-on-yellow',
        lime: 'bg-main-support-surface-lime text-main-support-on-surface-on-lime',
        green: 'bg-main-support-surface-green text-main-support-on-surface-on-green',
        cyan: 'bg-main-support-surface-cyan text-main-support-on-surface-on-cyan',
        blue: 'bg-main-support-surface-blue text-main-support-on-surface-on-blue',
        indigo: 'bg-main-support-surface-indigo text-main-support-on-surface-on-indigo',
        purple: 'bg-main-support-surface-purple text-main-support-on-surface-on-purple',
        outline: 'border border-border bg-transparent text-foreground',
      },
    },
    defaultVariants: {
      color: 'secondary',
    },
  },
)

const notificationBadgeVariants = cva('rounded-full bg-main-icon-error', {
  variants: {
    size: {
      sm: 'size-1',
      md: 'size-2',
      lg: 'size-3',
      xl: 'size-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>, Omit<VariantProps<typeof badgeVariants>, 'color'> {
  color?: BadgeColor
  leadingIcon?: React.ReactNode
  label?: React.ReactNode
}

function Badge({ className, color = 'secondary', leadingIcon, label, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ color }), className)} {...props}>
      {leadingIcon}
      {label !== undefined ? <span className="px-1">{label}</span> : children}
    </span>
  )
}

function NotificationBadge({
  className,
  size = 'md',
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof notificationBadgeVariants>) {
  return <div className={cn(notificationBadgeVariants({ size }), className)} {...props} />
}

export { Badge, NotificationBadge }
